import { useState, useEffect, useMemo, useCallback } from 'react';
import firebase from 'firebase/app';
import { message } from 'antd';

/** Matches payer `productCurrencyMapper` — supported product × currency pairs. */
export const PROCESSOR_PRODUCT_CURRENCY_ROWS = [
  { product: 'tipping', currency: 'GBP', defaultProcessor: 'cko' },
  { product: 'tipping', currency: 'EUR', defaultProcessor: 'stripe' },
  { product: 'tipping', currency: 'USD', defaultProcessor: 'stripeus' },
  { product: 'giving', currency: 'GBP', defaultProcessor: 'cko' },
  { product: 'giving', currency: 'EUR', defaultProcessor: 'stripe' },
  { product: 'giving', currency: 'USD', defaultProcessor: 'stripeus' },
  { product: 'payment', currency: 'GBP', defaultProcessor: 'cko' },
  { product: 'payment', currency: 'EUR', defaultProcessor: 'stripe' },
  { product: 'payment', currency: 'USD', defaultProcessor: 'stripeus' },
];

// stripegb = GB Stripe account (acct_1SazY08x6o0jlIZC), GBP on Stripe, MoR. Selecting it
// for a *_GBP row flips web GBP from cko to the GB Stripe account — no deploy needed.
export const PROCESSOR_KEYS = ['cko', 'stripe', 'stripeus', 'stripegb', 'internal'];

export function processorDocumentId(product, currency) {
  const c = String(currency || '').trim().toUpperCase();
  return `${product}_${c}`;
}

export function useAdminProcessors() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    const db = firebase.firestore();
    return db.collection('processors').onSnapshot(
      (snapshot) => {
        const list = snapshot.docs.map((doc) => {
          const d = doc.data();
          return {
            id: doc.id,
            product: d.product,
            currency: d.currency,
            processor: d.processor,
            status: d.status,
            createdAt: d.createdAt?.toDate?.() ?? d.createdAt,
            updatedAt: d.updatedAt?.toDate?.() ?? d.updatedAt,
          };
        });
        list.sort((a, b) => a.id.localeCompare(b.id));
        setRows(list);
        setLoading(false);
      },
      (err) => {
        console.error('processors listener', err);
        message.error('Failed to load processors');
        setLoading(false);
      },
    );
  }, []);

  useEffect(() => {
    const unsub = load();
    return () => unsub();
  }, [load]);

  const existingIds = useMemo(() => new Set(rows.map((r) => r.id)), [rows]);

  const saveProcessor = async ({ id, product, currency, processor, status }) => {
    const db = firebase.firestore();
    const c = String(currency || '').trim().toUpperCase();
    const docId = id || processorDocumentId(product, c);
    const ref = db.collection('processors').doc(docId);
    const snap = await ref.get();

    const payload = {
      product,
      currency: c,
      processor,
      status,
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    };
    if (!snap.exists) {
      payload.createdAt = firebase.firestore.FieldValue.serverTimestamp();
    }

    await ref.set(payload, { merge: true });
    message.success(snap.exists ? 'Processor updated' : 'Processor created');
  };

  return {
    rows,
    loading,
    existingIds,
    saveProcessor,
  };
}
